"use client";

import { useDebounce } from "@dc/ui/hooks/useDebounce";
import Cookies from "js-cookie";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthBackendId } from "@/app/providers/AuthBackendModeProvider/AuthBackendContext";
import { useAbility } from "@/hooks/useAbility";
import { EVENTS, useAnalytics } from "@/hooks/useAnalytics";
import { useOrganization } from "@/hooks/useOrganization";
import { getAuthBackend } from "@/lib/authBackend/resolve";
import { type Organization, useOrganizationsInfinite } from "../useOrganizationsInfinite/useOrganizationsInfinite";
const SEARCH_DEBOUNCE_MS = 500;
const MIN_ORGS_FOR_SEARCH = 5;
export type { Organization };
interface OrganizationEntitlement {
  status?: string;
}
function isNotEntitled(entitlement: object | undefined): boolean {
  return (entitlement as OrganizationEntitlement | undefined)?.status === "not_entitled";
}
export interface UseOrgSwitcherReturn {
  organizations: Organization[];
  currentOrganization: Organization | null;
  currentOrganizationId: string | null;
  totalCount: number;
  hasMore: boolean;
  isPending: boolean;
  isFetchingMore: boolean;
  showSearch: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  loadMore: () => void;
  selectOrganization: (org: Organization) => void;
  handleLogout: () => Promise<void>;
  isLoggingOut: boolean;
  /** Organization id -> disabled reason, derived from entitlement. Always empty for a System Admin. */
  disabledOrganizations: Record<string, string>;
}
export function useOrgSwitcher(): UseOrgSwitcherReturn {
  const {
    currentOrganization,
    currentOrganizationId,
    setCurrentOrganization
  } = useOrganization();
  const {
    trackEvent
  } = useAnalytics();
  const authBackendId = useAuthBackendId();
  const t = useTranslations("navigation.org_switcher");
  const {
    can
  } = useAbility();
  // System Admins routinely open many customer orgs for support; skipping the include (not just
  // ignoring the result) avoids a pointless entitlement check — cache read at minimum, a live
  // Trimble call on a miss — on every one of those opens.
  const isSystemAdmin = can("manage", {
    scopeType: "SYSTEM"
  });
  const [searchValue, setSearchValue] = useState("");
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [totalCountFromFirstLoad, setTotalCountFromFirstLoad] = useState<number | null>(null);
  const debouncedSearch = useDebounce(searchValue.trim(), SEARCH_DEBOUNCE_MS);
  useEffect(() => {
    if (debouncedSearch) {
      trackEvent(EVENTS.SEARCH_QUERY, {
        search_query: debouncedSearch,
        location: "organization switcher"
      });
    }
  }, [debouncedSearch, trackEvent]);
  const {
    data,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useOrganizationsInfinite({
    search: debouncedSearch || undefined,
    include: isSystemAdmin ? undefined : "entitlement"
  });
  const organizations = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.data ?? []);
  }, [data?.pages]);
  const disabledOrganizations = useMemo(() => {
    if (isSystemAdmin) return {};
    return organizations.reduce<Record<string, string>>((acc, org) => {
      if (isNotEntitled(org.attributes?.entitlement)) {
        acc[org.id] = t("license_inactive");
      }
      return acc;
    }, {});
  }, [organizations, isSystemAdmin, t]);
  const totalCount = data?.pages[0]?.meta?.total ?? 0;

  // Store the total count from the first load (without search) to determine if search should be shown
  useEffect(() => {
    if (totalCountFromFirstLoad === null && !debouncedSearch && data?.pages[0]?.meta?.total !== undefined) {
      setTotalCountFromFirstLoad(data.pages[0].meta.total);
    }
  }, [data?.pages, debouncedSearch, totalCountFromFirstLoad]);
  const showSearch = (totalCountFromFirstLoad ?? 0) > MIN_ORGS_FOR_SEARCH;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const selectOrganization = useCallback((org_0: Organization) => {
    setCurrentOrganization(org_0);
  }, [setCurrentOrganization]);
  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await getAuthBackend().signOut({
        global: true
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      Cookies.set("x-org-id", "", {
        path: "/",
        maxAge: 0
      });
      Cookies.set("x-team-id", "", {
        path: "/",
        maxAge: 0
      });
      // Trimble's logout() calls window.location.replace(end_session_endpoint) before
      // resolving. Calling assign here in the same JS tick would override that navigation,
      // preventing Trimble from ever reaching its session termination endpoint.
      if (authBackendId !== "trimble") {
        window.location.assign("/login");
      }
    }
  }, [authBackendId]);
  return {
    organizations,
    currentOrganization,
    currentOrganizationId,
    totalCount,
    hasMore: !!hasNextPage,
    isPending,
    isFetchingMore: isFetchingNextPage,
    showSearch,
    searchValue,
    setSearchValue,
    loadMore,
    selectOrganization,
    handleLogout,
    isLoggingOut,
    disabledOrganizations
  };
}